/**
 * Document Branding settings — Settings → Company Branding → Document branding.
 *
 * Admins / super-users customise the colour palette applied across:
 *   • Estimate PDF (estimate_pdf.py)
 *   • Invoice PDF (invoice_pdf.py)
 *   • Public Estimate web page (PublicEstimate.jsx)
 *   • Future Job Report PDF
 *
 * Includes a live preview pane so changes are visible immediately before
 * saving. Saving PUTs to `/api/company/document-branding`, which validates
 * every value is a valid hex colour and persists onto `company.document_branding`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { Palette, RotateCcw, Loader2, Save } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const DEFAULTS = {
  primary_color: '#1E88E5',
  accent_color: '#10B981',
  text_color: '#0F172A',
  muted_color: '#64748B',
  background_color: '#FFFFFF',
  subtle_bg: '#F8FAFC',
  border_color: '#E2E8F0',
};

const FIELDS = [
  { key: 'primary_color', label: 'Primary',
    hint: 'Header band, total line, brand accents.' },
  { key: 'accent_color', label: 'Accent',
    hint: 'Accept button, deposit highlight, win metrics.' },
  { key: 'text_color', label: 'Body text',
    hint: 'Default colour for paragraphs, totals, line items.' },
  { key: 'muted_color', label: 'Muted text',
    hint: 'Labels (FROM, TO, ESTIMATE), sub-copy, hints.' },
  { key: 'background_color', label: 'Card background',
    hint: 'Main page surface — usually pure white.' },
  { key: 'subtle_bg', label: 'Subtle panels',
    hint: 'Zebra striping, T&C box, soft sections.' },
  { key: 'border_color', label: 'Borders',
    hint: 'Dividers and hairlines between sections.' },
];

export default function DocumentBranding({ token, company }) {
  const { toast } = useToast();
  const [palette, setPalette] = useState({ ...DEFAULTS, ...(company?.document_branding || {}) });
  const [saving, setSaving] = useState(false);
  // Track whether the current local palette differs from server
  const initial = useMemo(() => ({ ...DEFAULTS, ...(company?.document_branding || {}) }), [company?.document_branding]);
  const dirty = useMemo(() => Object.keys(palette).some(k => palette[k] !== initial[k]), [palette, initial]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/company/document-branding`, palette, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPalette({ ...DEFAULTS, ...(res.data.document_branding || {}) });
      toast({ title: 'Document branding saved', description: 'PDFs and customer-facing pages will use the new colours immediately.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => setPalette({ ...DEFAULTS });

  // Sample numbers for the preview pane so designers see realistic layout.
  const sample = {
    estimateNumber: 'EST-2026-0127',
    title: 'Drywall repair — basement',
    customer: 'Sample Customer Ltd.',
    subtotal: 850.0,
    tax: 110.5,
    grand: 960.5,
    deposit: 240.13,
  };

  return (
    <div className="space-y-6" data-testid="document-branding">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> Document branding</CardTitle>
          <CardDescription>
            Customise the colours of every PDF (Estimate, Invoice, Report) and
            the public Accept / Decline page customers see when you email them
            a quote. Changes apply company-wide; admins only.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* ── Inputs (2 of 5 cols) ──────────────────────────────── */}
            <div className="lg:col-span-2 space-y-4">
              {FIELDS.map(({ key, label, hint }) => (
                <div key={key} data-testid={`branding-field-${key}`}>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={palette[key]}
                      onChange={(e) => setPalette({ ...palette, [key]: e.target.value })}
                      style={{ width: 44, height: 36, border: 'none', borderRadius: 6, padding: 0, cursor: 'pointer' }}
                      data-testid={`branding-picker-${key}`}
                    />
                    <Input
                      value={palette[key]}
                      onChange={(e) => setPalette({ ...palette, [key]: e.target.value })}
                      placeholder="#RRGGBB"
                      className="font-mono text-sm"
                      data-testid={`branding-hex-${key}`}
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{hint}</p>
                </div>
              ))}

              <div className="flex gap-2 pt-4 border-t border-slate-700">
                <Button onClick={save} disabled={saving || !dirty} className="flex-1" data-testid="branding-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save palette
                </Button>
                <Button variant="outline" onClick={resetDefaults} disabled={saving} data-testid="branding-reset">
                  <RotateCcw className="w-4 h-4 mr-2" /> Defaults
                </Button>
              </div>
            </div>

            {/* ── Live preview (3 of 5 cols) ────────────────────────── */}
            <div className="lg:col-span-3" data-testid="branding-preview">
              <Label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">Live preview — Estimate</Label>
              <div style={{ background: palette.subtle_bg, padding: 20, borderRadius: 10, border: `1px solid ${palette.border_color}` }}>
                <div style={{ background: palette.background_color, borderRadius: 8, border: `1px solid ${palette.border_color}`, overflow: 'hidden' }}>
                  {/* Brand band */}
                  <div style={{ background: palette.primary_color, height: 4 }} />
                  <div style={{ padding: 16 }}>
                    {/* Header row */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: palette.muted_color, margin: 0 }}>Estimate</p>
                        <p style={{ fontSize: 16, fontWeight: 700, color: palette.text_color, margin: 0 }}>{sample.estimateNumber}</p>
                        <p style={{ fontSize: 12, color: palette.text_color, margin: '2px 0 0' }}>{sample.title}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 10, color: palette.muted_color, margin: 0 }}>Valid until</p>
                        <p style={{ fontSize: 12, fontWeight: 600, color: palette.text_color, margin: 0 }}>Mar 9, 2026</p>
                      </div>
                    </div>

                    <div style={{ borderTop: `1px solid ${palette.border_color}`, paddingTop: 10, marginBottom: 10 }}>
                      <p style={{ fontSize: 10, color: palette.muted_color, marginBottom: 2 }}>PREPARED FOR</p>
                      <p style={{ fontSize: 12, color: palette.text_color, margin: 0 }}>{sample.customer}</p>
                    </div>

                    {/* Totals mini-table */}
                    <table style={{ width: '100%', fontSize: 12, color: palette.text_color }}>
                      <tbody>
                        <tr style={{ background: palette.subtle_bg }}>
                          <td style={{ padding: 6 }}>Labour</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>$650.00</td>
                        </tr>
                        <tr>
                          <td style={{ padding: 6 }}>Materials</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>$200.00</td>
                        </tr>
                        <tr>
                          <td style={{ padding: 6, color: palette.muted_color }}>Subtotal</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>${sample.subtotal.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: 6, color: palette.muted_color }}>Tax (13%)</td>
                          <td style={{ padding: 6, textAlign: 'right' }}>${sample.tax.toFixed(2)}</td>
                        </tr>
                        <tr style={{ borderTop: `1px solid ${palette.border_color}` }}>
                          <td style={{ padding: '8px 6px', fontWeight: 700, color: palette.text_color }}>TOTAL</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, fontSize: 16, color: palette.primary_color }}>${sample.grand.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '4px 6px', color: palette.accent_color, fontWeight: 600 }}>Deposit on acceptance (25%)</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', color: palette.accent_color, fontWeight: 700 }}>${sample.deposit.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Accept/Decline preview */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                      <div style={{ flex: 1, background: palette.accent_color, color: '#FFFFFF', padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Accept estimate</div>
                      <div style={{ flex: 1, background: palette.background_color, color: palette.text_color, border: `1px solid ${palette.border_color}`, padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Decline</div>
                    </div>
                  </div>
                </div>
                <p style={{ fontSize: 10, color: palette.muted_color, marginTop: 8, textAlign: 'center' }}>This is what your customer sees on the public estimate page.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
